import React, { Fragment, useState, useEffect, useRef } from 'react';
import { IconButton } from '@material-ui/core';
import { Button } from 'reactstrap';
import DateRange from '@material-ui/icons/DateRangeOutlined';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { withTranslation } from 'react-i18next';
import format from 'date-fns/format';

const DateSelection = (props) => {
  const [selectionComplete, toggleSelectionComplete] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [firstClick, setFirstClick] = useState(true);
  const [showErrorDate, setShowErrorDate] = useState(false);
  const [showErrorPeriod, setShowErrorPeriod] = useState(false);
  const [clickSucess, setClickSucess] = useState(false);
  const rangeSelectionRef = useRef();
  const {
    changeHandler,
    months,
    icon,
    minDate,
    initialSelectedDate,
    rangeSelection,
    t,
    popperPlacement,
    maxDate,
    translate,
    shouldReset,
    maxPeriodInDays,
  } = props;

  useEffect(() => {
    setStartDate(initialSelectedDate);
  }, []);

  useEffect(() => {
    if (shouldReset) {
      setStartDate();
      setEndDate();
      toggleSelectionComplete(false);
      setFirstClick(true);
    }
  }, [shouldReset]);

  useEffect(() => {
    if (clickSucess) {
      setStartDate();
      setEndDate();
      toggleSelectionComplete(false);
      setFirstClick(true);
      setClickSucess(false);
    }
  }, [clickSucess]);

  const handleDateChange = (date) => {
    if (!rangeSelection) {
      setStartDate(date);
      changeHandler(date);
      return;
    }
    if (!selectionComplete && firstClick) {
      // on first date selection, set the start date
      setStartDate(date);
      setFirstClick(!firstClick);
      return;
    }

    if (!selectionComplete && startDate && !endDate && date >= startDate) {
      setEndDate(date);
      toggleSelectionComplete(true);
      return;
    }

    if (!selectionComplete && startDate && !endDate && date < startDate) {
      setStartDate(date);
      return;
    }

    if (selectionComplete && startDate && endDate) {
      // on third date selection, begin selection of a new date range
      // reset the start date and clear the end date.
      setShowErrorDate(false);
      setShowErrorPeriod(false);
      setStartDate(date);
      setEndDate(undefined);
      toggleSelectionComplete(false);
    }
  };

  const sameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const handleSelect = (date) => {
    // onChange is not fired if selecting same date - workaround to fire handleDateChange
    if (
      !selectionComplete &&
      startDate &&
      !endDate &&
      sameDay(date, startDate)
    ) {
      handleDateChange(date);
    }
  };

  const repeatClickHandler = () => {
    if (maxPeriodInDays) {
      const difference = Math.abs(endDate - startDate);
      const period = difference / (1000 * 3600 * 24);
      if (endDate <= startDate) {
        setShowErrorDate(true);
        return;
      }
      if (period >= maxPeriodInDays) {
        setShowErrorPeriod(true);
        return;
      }
    }
    setClickSucess(true);
    changeHandler(startDate, endDate);
    rangeSelectionRef.current.setOpen(false);
  };

  // eslint-disable-next-line react/prop-types
  const MyContainer = ({ children }) => (
    <div className="date-selection">
      <div className="row">{children}</div>
      <div className="action-row d-flex flex-column justify-content-center">
        <p>{t(translate.instruction)}</p>
        <p>
          {startDate ? `${format(startDate, 'dd/MM/yyyy')}` : null}
          {endDate ? ` - ${format(endDate, 'dd/MM/yyyy')}` : null}
        </p>
        {showErrorDate ? (
          <div className="alert alert-danger" role="alert">
            {t('print-chat.date-error')}
          </div>
        ) : null}
        {showErrorPeriod ? (
          <div className="alert alert-danger" role="alert">
            {t('print-chat.period-error')}
          </div>
        ) : null}
        <Button
          color="success"
          onClick={repeatClickHandler}
          disabled={!selectionComplete}
        >
          {t(translate.button)}
        </Button>
      </div>
    </div>
  );

  return (
    <MuiThemeProvider>
      <Fragment>
        <div className="date-picker icon">
          {rangeSelection ? (
            <DatePicker
              showPopperArrow
              onChange={handleDateChange}
              onSelect={handleSelect}
              locale={ptBR}
              shouldCloseOnSelect={!rangeSelection}
              popperPlacement={popperPlacement || 'top-end'}
              minDate={minDate}
              selectsEnd={Boolean(startDate)}
              startDate={startDate}
              endDate={endDate}
              maxDate={maxDate}
              customInput={<IconButton size="medium">{icon}</IconButton>}
              monthsShown={months}
              calendarContainer={MyContainer}
              ref={rangeSelectionRef}
            />
          ) : (
            <DatePicker
              showPopperArrow={false}
              selected={startDate}
              onChange={handleDateChange}
              onSelect={handleSelect}
              locale={ptBR}
              shouldCloseOnSelect={!rangeSelection}
              popperPlacement={popperPlacement || 'top-end'}
              minDate={minDate}
              endDate={endDate}
              maxDate={maxDate}
              customInput={<IconButton size="medium">{icon}</IconButton>}
              monthsShown={months}
            />
          )}
        </div>
      </Fragment>
    </MuiThemeProvider>
  );
};

DateSelection.propTypes = {
  changeHandler: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  months: PropTypes.number,
  minDate: PropTypes.oneOfType(PropTypes.date),
  icon: PropTypes.oneOfType(PropTypes.shape),
  initialSelectedDate: PropTypes.oneOfType(PropTypes.date),
  rangeSelection: PropTypes.bool,
  popperPlacement: PropTypes.string,
  maxDate: PropTypes.oneOfType(PropTypes.date),
  translate: PropTypes.oneOfType(PropTypes.shape),
  shouldReset: PropTypes.bool,
  maxPeriodInDays: PropTypes.bool,
};

DateSelection.defaultProps = {
  months: 1,
  icon: <DateRange />,
  minDate: null,
  initialSelectedDate: new Date(),
  rangeSelection: false,
  popperPlacement: 'top-end',
  maxDate: null,
  translate: {
    instruction: 'repeat-menu.instruction',
    button: 'general.repeat',
  },
  shouldReset: false,
  maxPeriodInDays: false,
};

export default withTranslation('common')(DateSelection);
